import { Box, Grid2, Tab, TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  checkCanUpdate,
  noOnlySpaces,
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../../../utilities/helper";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import SearchableSelect from "../../../components/SearchableSelect";
import CustomSwitch from "../../../components/CustomSwitch/CustomSwitch";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import SiteCaps from "./SiteCaps/SiteCaps";
import {
  addUpdateSiteService,
  getOnLoadSiteDataService,
} from "../../../services/siteManagamentService";
import ConfirmSite from "./ConfirmSite/ConfirmSite";
import SiteLanguages from "./SiteLanguages/SiteLanguages";

const AddUpdateSiteManagement = () => {
  const id = useLocation()?.state?.id;
  const roleId = localStorage.getItem("roleId");

  const [load, setLoad] = useState(false);

  const [canUpdateSite, setCanUpdateSite] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [tabValue, setTabValue] = useState("details");
  const [loadData, setLoadData] = useState({
    countryList: [],
    timeZoneList: [],
    depotList: [],
    resupplyList: [],
    siteNumberLength: 0,
    isScreeningOpen: false,
    isRandomizationOpen: false,
    showManageSiteInventoryTab: false,
    showSiteCapsTab: false,
    showLanguageTab: false,
    disableTabs: false,
    studyTypeEnum: {
      eCOA: null,
      irt: null,
      joint: null,
    },
    siteName: "",
  });

  // Confirmation related states
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  const navigate = useNavigate();

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const access_token = localStorage.getItem("access_token");
  const role = localStorage.getItem("role");
  const studyType = localStorage.getItem("studyType");

  const {
    countryList,
    depotList,
    resupplyList,
    timeZoneList,
    siteNumberLength,
    isRandomizationOpen,
    isScreeningOpen,
    showManageSiteInventoryTab,
    showSiteCapsTab,
    showLanguageTab,
    disableTabs,
    siteName,
    studyTypeEnum: { eCOA, irt, joint },
  } = loadData;

  const createSiteSchema = yup.object().shape({
    name: noOnlySpaces(yup, "Name")
      .required("Name is required")
      .max(450, "Maximum 450 characters allowed"),
    siteNumber: yup
      .string()
      .matches(
        new RegExp(`^\\d{${siteNumberLength}}$`),
        `Site number must be exactly ${siteNumberLength} digits`
      )
      .required("Site Number is required"),
    primaryContact: noOnlySpaces(yup, "Primary Contact").required(
      "Primary Contact is required"
    ),
    investigator: noOnlySpaces(yup, "Investigator")
      .required("Investigator is required")
      .max(50, "Maximum 50 characters allowed"),
    address1: noOnlySpaces(yup, "Address 1").required("Address is required"),
    address2: noOnlySpaces(yup, "Address 2"),
    address3: noOnlySpaces(yup, "Address 3"),
    city: noOnlySpaces(yup, "City").required("City is required"),
    state: noOnlySpaces(yup, "State").required("State is required"),
    zip: yup
      .string()
      .matches(/^\d{5,6}$/, "Zip code must be 5 or 6 digits")
      .required("Zip is required"),
    country: noOnlySpaces(yup, "Country").required("Country is required"),
    phoneNumber: yup
      .string()
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
    faxNumber: yup
      .string()
      .nullable()
      .notRequired()
      .test("faxNumber", "Fax number must be 7 to 10 digits", (value) => {
        if (!value) return true; // skip validation if empty
        return /^\d{7,10}$/.test(value);
      }),
    timeZone: yup.string().required("Time Zone is required"),
    depot: yup.string().required("Depot is required"),
    returnDepot: yup.string().required("Return Depot is required"),
    resupplyType: yup.string().required("Resupply Type is required"),
    drugOrderingEnabled: yup.boolean(),
    isActive: yup.boolean(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createSiteSchema),
    defaultValues: {
      siteNumber: "",
      investigator: "",
      name: "",
      primaryContact: "",
      address1: "",
      address2: "",
      address3: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      phoneNumber: "",
      faxNumber: "",
      timeZone: "",
      depot: "",
      resupplyType: "",
      drugOrderingEnabled: false,
      returnDepot: "",
      isActive: false,
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    if (id) {
      getOnLoadData(id);
    } else {
      getOnLoadData(null);
    }
  }, [access_token, id]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanEditSites"
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateSite(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateSite(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (canUpdateSite) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateSite]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getOnLoadData = async (id = null) => {
    try {
      setLoad(true);
      const res = await getOnLoadSiteDataService(access_token, roleId, id);
      if (res?.status == 200) {
        if (res?.data?.result?.allViewBagData) {
          setLoad(false);
          const data = res?.data?.result?.allViewBagData;
          setLoadData({
            countryList: data?.countryList,
            depotList: data?.depotList,
            resupplyList: data?.resupplyTypeList,
            timeZoneList: data?.allTimeZones,
            siteNumberLength: Number(data?.siteNumberLength) || 5,
            isRandomizationOpen:
              res?.data?.result?.siteDto?.isRandomizationOpen || false,
            isScreeningOpen:
              res?.data?.result?.siteDto?.isScreeningOpen || false,
            showManageSiteInventoryTab:
              res?.data?.result?.canViewSiteKits || false,
            showSiteCapsTab: res?.data?.result?.canEditSiteCaps || false,
            showLanguageTab: res?.data?.result?.canEditSiteLanguages || false,
            disableTabs: data?.isNewSite === "false" ? false : true || false,
            siteName: res?.data?.result?.siteDto?.name || "",
            studyTypeEnum: {
              eCOA: res?.data?.result?.studyTypeEnum?.find(
                (item) => item?.name === "eCOA"
              )?.value,
              irt: res?.data?.result?.studyTypeEnum?.find(
                (item) => item?.name === "Irt"
              )?.value,
              joint: res?.data?.result?.studyTypeEnum?.find(
                (item) => item?.name === "Joint"
              )?.value,
            },
          });
          if (id) {
            const siteData = res?.data?.result?.siteDto;
            if (siteData) {
              reset({
                name: siteData?.name,
                primaryContact: siteData?.primaryContact,
                address1: siteData?.address1,
                address2: siteData?.address2 || "",
                address3: siteData?.address3 || "",
                city: siteData?.city,
                state: siteData?.state,
                zip: siteData?.zip,
                country: siteData?.countryId,
                phoneNumber: siteData?.phoneNumber,
                faxNumber: siteData?.faxNumber || "",
                timeZone: siteData?.timeZone,
                depot: siteData?.depotId,
                resupplyType: siteData?.resupplyTypeId,
                drugOrderingEnabled: siteData?.isDrugOrderingEnabled,
                isActive: siteData?.isActive,
                siteNumber: siteData?.siteNumber,
                investigator: siteData?.investigator,
                returnDepot: siteData?.returnDepot,
              });
            }
          }
        }
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err?.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const addUpdateSiteDetails = async (data) => {
    const newData = {
      siteNumber: data?.siteNumber,
      name: data?.name,
      address1: data?.address1,
      address2: data?.address2,
      address3: data?.address3,
      countryId: data?.country,
      countryName: countryList?.find((item) => item?.id === data?.country)
        ?.name,
      state: data?.state,
      city: data?.city,
      zip: data?.zip,
      timeZone: data?.timeZone,
      phoneNumber: data?.phoneNumber,
      faxNumber: data?.faxNumber,
      primaryContact: data?.primaryContact,
      depotId: data?.depot,
      depotName: depotList?.find((item) => item?.id === data?.depot)?.name,
      returnDepot: data?.returnDepot,
      resupplyTypeId: data?.resupplyType,
      resupplyTypeName: resupplyList?.find(
        (item) => item?.id === data?.resupplyType
      )?.name,
      patientDOBFormatId: null,
      isDrugOrderingEnabled: data?.drugOrderingEnabled,
      isActive: data?.isActive,
      notes: null,
      investigator: data?.investigator,
      isRandomizationOpen: isRandomizationOpen,
      isScreeningOpen: isScreeningOpen,
    };
    if (id) {
      newData["id"] = id;
    }
    setLoad(true);
    try {
      const res = await addUpdateSiteService(access_token, newData, roleId);
      if (
        res?.data?.message === "Error Found!" &&
        res?.data?.result[0] === "Duplicate site name found."
      ) {
        setLoad(false);
        notifyWarning("Site name already exists. Please use a different name.");
      } else if (res?.status === 200) {
        if (res?.data?.message === "Site number must be unique!") {
          setLoad(false);
          notifyWarning(
            "Site number already exists. Please use a different number."
          );
        } else {
          setLoad(false);
          notifySuccess(`Site is ${id ? "updated" : "created"} successfully!`);
          reset();
          setConfirmData(res?.data?.result);
          setShowConfirm(true);
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const phoneNumberRegister = register("phoneNumber");
  const zipRegister = register("zip");
  const faxNumberRegister = register("faxNumber");
  const siteNumberRegister = register("siteNumber");

  return (
    <>
      {showConfirm ? (
        <ConfirmSite data={confirmData} setShowConfirm={setShowConfirm} />
      ) : (
        <>
          {load ? (
            <Loader />
          ) : (
            <div className="content-body">
              <p className="bread-crumbs-link-container">
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(e, isDirty, "", navigate)
                  }
                >
                  Home
                </button>{" "}
                |{" "}
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "site-management",
                      navigate
                    )
                  }
                >
                  Site Management
                </button>{" "}
                |
                <button
                  className="bread-crumbs-btn"
                  to="/addUpdate-site-management"
                >
                  {id ? "Edit Site" : "Add Site"}
                </button>
                -{" "}
                <b style={{ color: "gray" }}>
                  {localStorage?.getItem("studyName")}
                </b>
              </p>

              {/* Tab Section */}
              <Box sx={{ width: "100%", typography: "body1", marginTop: "2%" }}>
                <TabContext value={tabValue}>
                  {id && (
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <TabList
                        onChange={handleTabChange}
                        aria-label="Site Tabs"
                        TabIndicatorProps={{ style: { display: "none" } }}
                        sx={{
                          "& .MuiTab-root": {
                            textTransform: "none",
                            wordSpacing: "-2px",
                            fontWeight: 500,
                            borderRadius: "999px",
                            minHeight: "40px",
                            px: 3,
                            py: 1,
                            mx: 0.5,
                            // mt: 2,
                            transition: "all 0.3s ease",
                            color: "text.secondary",
                            bgcolor: "transparent",
                          },
                          "& .Mui-selected": {
                            bgcolor: "primary.main",
                            color: "white !important",
                            boxShadow: 2,
                          },
                        }}
                      >
                        <Tab label="Site Details" value="details" />
                        {/* {showManageSiteInventoryTab && studyType !== eCOA && ( */}
                        <Tab
                          label="Manage Site Inventory"
                          value="inventory"
                          disabled={disableTabs}
                        />
                        {/* )} */}
                        {/* {showSiteCapsTab && studyType !== irt && ( */}
                        <Tab
                          label="Site Caps"
                          value="caps"
                          disabled={disableTabs}
                        />
                        {/* )} */}
                        {/* {showLanguageTab && studyType !== irt && ( */}
                        <Tab
                          label="Site Languages"
                          value="siteLanguages"
                          disabled={disableTabs}
                        />
                        {/* )} */}
                      </TabList>
                    </Box>
                  )}
                  <TabPanel value="details" sx={{ padding: "30px 0px" }}>
                    {/* Site Details */}
                    <>
                      <Grid2
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          width: "100%",
                          margin: "0 auto",
                          marginTop: "-2%",
                        }}
                      >
                        <Grid2 item md={12}>
                          <p className="page-heading">Site Details</p>
                        </Grid2>
                      </Grid2>

                      <Box
                        component="form"
                        autoComplete="off"
                        sx={{ height: "auto", width: "100%" }}
                        onSubmit={handleSubmit(addUpdateSiteDetails)}
                      >
                        <Grid2
                          container
                          alignItems="center"
                          justifyContent={"space-between"}
                          sx={{ width: "100%", margin: "0 auto" }}
                          gap={1}
                        >
                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Name
                                <span>*</span>
                              </p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("name")}
                              />
                            </div>
                            {errors.name && (
                              <div className="error-container">
                                {errors.name.message}
                              </div>
                            )}
                          </Grid2>
                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Site Number
                                <span>*</span>
                              </p>
                              <TextField
                                fullWidth
                                disabled={isReadOnly}
                                size="small"
                                type={"text"}
                                {...siteNumberRegister}
                                onChange={(e) => {
                                  let value = e.target.value.replace(/\D/g, "");
                                  if (value.length > siteNumberLength)
                                    value = value.slice(0, siteNumberLength);
                                  e.target.value = value;
                                  siteNumberRegister.onChange(e);
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            </div>
                            {errors.siteNumber && (
                              <div className="error-container">
                                {errors.siteNumber.message}
                              </div>
                            )}
                          </Grid2>
                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Primary Contact
                                <span>*</span>
                              </p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("primaryContact")}
                              />
                            </div>
                            {errors.primaryContact && (
                              <div className="error-container">
                                {errors.primaryContact.message}
                              </div>
                            )}
                          </Grid2>
                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Investigator
                                <span>*</span>
                              </p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("investigator")}
                              />
                            </div>
                            {errors.investigator && (
                              <div className="error-container">
                                {errors.investigator.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Address 1<span>*</span>
                              </p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("address1")}
                              />
                            </div>
                            {errors.address1 && (
                              <div className="error-container">
                                {errors.address1.message}
                              </div>
                            )}
                          </Grid2>
                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">Address 2</p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("address2")}
                              />
                            </div>
                            {errors.address2 && (
                              <div className="error-container">
                                {errors.address2.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">Address 3</p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("address3")}
                              />
                            </div>
                            {errors.address3 && (
                              <div className="error-container">
                                {errors.address3.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                City <span>*</span>
                              </p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("city")}
                              />
                            </div>
                            {errors.city && (
                              <div className="error-container">
                                {errors.city.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                State <span>*</span>
                              </p>
                              <TextField
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                type={"text"}
                                {...register("state")}
                              />
                            </div>
                            {errors.state && (
                              <div className="error-container">
                                {errors.state.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Zip <span>*</span>
                              </p>
                              <TextField
                                fullWidth
                                disabled={isReadOnly}
                                size="small"
                                type="text"
                                {...zipRegister}
                                onChange={(e) => {
                                  let value = e.target.value.replace(/\D/g, "");
                                  if (value.length > 6)
                                    value = value.slice(0, 6);
                                  e.target.value = value;
                                  zipRegister.onChange(e);
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            </div>
                            {errors.zip && (
                              <div className="error-container">
                                {errors.zip.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Country <span>*</span>
                              </p>
                              <SearchableSelect
                                control={control}
                                data={countryList}
                                name={"country"}
                                isReadOnly={isReadOnly}
                                optionKey="id"
                                optionLabel="name"
                              />
                            </div>
                            {errors.country && (
                              <div className="error-container">
                                {errors.country.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Phone Number <span>*</span>
                              </p>
                              <TextField
                                fullWidth
                                size="small"
                                disabled={isReadOnly}
                                type="text"
                                {...phoneNumberRegister}
                                onChange={(e) => {
                                  let value = e.target.value.replace(/\D/g, "");
                                  if (value.length > 10)
                                    value = value.slice(0, 10);
                                  e.target.value = value;
                                  phoneNumberRegister.onChange(e);
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            </div>
                            {errors.phoneNumber && (
                              <div className="error-container">
                                {errors.phoneNumber.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">Fax Number</p>
                              <TextField
                                fullWidth
                                disabled={isReadOnly}
                                size="small"
                                type="text"
                                {...faxNumberRegister}
                                onChange={(e) => {
                                  let value = e.target.value.replace(/\D/g, "");
                                  if (value.length > 10)
                                    value = value.slice(0, 10);
                                  e.target.value = value;
                                  faxNumberRegister.onChange(e);
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            </div>
                            {errors.faxNumber && (
                              <div className="error-container">
                                {errors.faxNumber.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Time Zone <span>*</span>
                              </p>
                              <SearchableSelect
                                control={control}
                                data={timeZoneList}
                                name={"timeZone"}
                                isReadOnly={isReadOnly}
                                optionKey="value"
                                optionLabel="key"
                              />
                            </div>
                            {errors.timeZone && (
                              <div className="error-container">
                                {errors.timeZone.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Depot <span>*</span>
                              </p>
                              <SearchableSelect
                                control={control}
                                data={depotList}
                                name={"depot"}
                                isReadOnly={isReadOnly}
                                optionKey="id"
                                optionLabel="name"
                              />
                            </div>
                            {errors.depot && (
                              <div className="error-container">
                                {errors.depot.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Return Depot <span>*</span>
                              </p>
                              <SearchableSelect
                                control={control}
                                data={depotList}
                                name={"returnDepot"}
                                isReadOnly={isReadOnly}
                                optionKey="id"
                                optionLabel="name"
                              />
                            </div>
                            {errors.returnDepot && (
                              <div className="error-container">
                                {errors.returnDepot.message}
                              </div>
                            )}
                          </Grid2>

                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Resupply Type <span>*</span>
                              </p>
                              <SearchableSelect
                                control={control}
                                data={resupplyList}
                                name={"resupplyType"}
                                isReadOnly={isReadOnly}
                                optionKey="id"
                                optionLabel="name"
                              />
                            </div>
                            {errors.resupplyType && (
                              <div className="error-container">
                                {errors.resupplyType.message}
                              </div>
                            )}
                          </Grid2>
                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles">
                                Drug Ordering Enabled
                              </p>
                              <Controller
                                name="drugOrderingEnabled"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <CustomSwitch
                                    isSwitch={field.value}
                                    disabled={isReadOnly}
                                    setSwitch={(val) => field.onChange(val)}
                                  />
                                )}
                              />
                            </div>
                          </Grid2>
                          <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                            <div>
                              <p className="labelStyles"> Active</p>
                              <Controller
                                name="isActive"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <CustomSwitch
                                    isSwitch={field.value}
                                    disabled={isReadOnly}
                                    setSwitch={(val) => field.onChange(val)}
                                  />
                                )}
                              />
                            </div>
                          </Grid2>
                        </Grid2>

                        <Grid2
                          alignItems={"end"}
                          justifyContent="end"
                          container
                          gap={3}
                          sx={{ marginTop: "2%" }}
                        >
                          <Grid2 item md={6}>
                            <button
                              type="button"
                              className="create-btn-outlined"
                              onClick={(e) => {
                                e.preventDefault();
                                handleUnsaveNavigation(
                                  e,
                                  isDirty,
                                  "site-management",
                                  navigate
                                );
                              }}
                            >
                              Cancel
                            </button>
                          </Grid2>
                          <Grid2 item md={6}>
                            {checkCanUpdate(id, isReadOnly, role) && (
                              <button
                                type="submit"
                                disabled={isReadOnly}
                                className={
                                  isReadOnly ? "disabled-btn" : "create-btn"
                                }
                              >
                                {id ? "Update" : "Save"}
                              </button>
                            )}
                          </Grid2>
                        </Grid2>
                      </Box>
                    </>
                  </TabPanel>
                  <TabPanel value="inventory" sx={{ padding: "30px 0px" }}>
                    <>
                      <Grid2
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          width: "100%",
                          margin: "0 auto",
                          marginTop: "-2%",
                        }}
                      >
                        <Grid2 item md={12}>
                          <p className="page-heading">Manage Site Inventory</p>
                        </Grid2>
                      </Grid2>
                      <div
                        style={{
                          display: "flex",
                          gap: "30px",
                          justifyContent: "space-between",
                          alignContent: "center",
                          padding: "0 30px",
                        }}
                      >
                        <button
                          className="create-btn-outlined big-btn"
                          onClick={() => {
                            navigate("/site-management-reconcile-drug", {
                              state: {
                                id: id,
                                name: siteName,
                              },
                            });
                          }}
                        >
                          Reconcile Drug At Site
                        </button>
                        <button
                          className="create-btn-outlined big-btn"
                          onClick={() => {
                            navigate("/site-management-site-return-depot", {
                              state: {
                                id: id,
                                name: siteName,
                              },
                            });
                          }}
                        >
                          Site Return Depot
                        </button>
                        <button
                          className="create-btn-outlined big-btn"
                          onClick={() => {
                            navigate("/individual-kit-status", {
                              state: {
                                id: id,
                                name: siteName,
                              },
                            });
                          }}
                        >
                          Kit Status Management
                        </button>
                      </div>
                    </>
                  </TabPanel>
                  <TabPanel value="caps" sx={{ padding: "30px 0px" }}>
                    <SiteCaps />
                  </TabPanel>
                  <TabPanel value="siteLanguages" sx={{ padding: "30px 0px" }}>
                    <SiteLanguages siteId={id} />
                  </TabPanel>
                </TabContext>
              </Box>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AddUpdateSiteManagement;
